export type CompletionSuggestionLike = {
  // Monaco CompletionItem labels are `string | CompletionItemLabel` — the
  // version-gating post-process emits the object shape ({ label, description })
  // for badged items, so the merge must accept both.
  readonly label: string | { readonly label: string };
  readonly insertText?: string;
  readonly kind?: number;
};

type MergeOptions<T extends CompletionSuggestionLike> = {
  /**
   * When two suggestions have the same key, keep the first one (default: true).
   */
  readonly preferFirst?: boolean;
  readonly key?: (item: T) => string;
};

const labelText = (label: CompletionSuggestionLike['label']): string =>
  typeof label === 'string' ? label : label?.label ?? '';

const defaultKey = (item: CompletionSuggestionLike): string =>
  labelText(item.label).trim().toLowerCase();

export function mergeCompletionSuggestions<T extends CompletionSuggestionLike>(
  first: readonly T[],
  second: readonly T[],
  options?: MergeOptions<T>
): T[] {
  const keyFn = options?.key ?? defaultKey;
  const preferFirst = options?.preferFirst ?? true;

  const seen = new Set<string>();
  const merged: T[] = [];

  const pushUnique = (item: T) => {
    const key = keyFn(item);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  };

  if (preferFirst) {
    first.forEach(pushUnique);
    second.forEach(pushUnique);
    return merged;
  }

  second.forEach(pushUnique);
  first.forEach(pushUnique);
  return merged;
}

